import React, { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { Tooltip, shouldEnableTooltip } from '../Tooltip/Tooltip';
import { BpDesignSystem } from '../../types';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { Box } from '../Box';
import { styled } from '../../styles';
import { ActiveDiv } from '../ActiveDiv';
import { Typography } from '../Typography';

export type CopyToClipboardProps = BpDesignSystem.PropsWithChildren<{
  valueToCopy: string;
  onCopy?: () => void;
  tooltipText?: string;
  /* Show tooltip only when ellipsis has appeared */
  tooltipOnlyWithEllipsis?: boolean;
  /* Component take 100% of parent element and base on it will add ellipsis if needed */
  enableEllipsis?: boolean;
  wrapText?: boolean;
}>;

const ICON_SIZE = 18;

export const CopyToClipboard: FC<CopyToClipboardProps> = ({
  children,
  valueToCopy,
  onCopy,
  tooltipText,
  tooltipOnlyWithEllipsis,
  enableEllipsis,
  dataTest,
  wrapText,
  ...props
}) => {
  const [showTooltip, setTooltipState] = useState(false);
  const [copied, setCopied] = useState(false);
  const typographyRef = useRef<HTMLElement>(null);

  const { intl } = useIntlBds();

  const onCopyHandler = useCallback(() => {
    copyToClipboard(valueToCopy);
    setCopied(true);
    onCopy?.();
  }, [onCopy, valueToCopy]);

  const onMouseEnter = useCallback(() => {
    if (tooltipOnlyWithEllipsis && typographyRef.current) {
      let ref = typographyRef.current;
      if (typographyRef.current.children.length > 0) {
        // If direct children is not a string, calculate reference base on children element
        // eslint-disable-next-line prefer-destructuring
        ref = typographyRef.current.children[0] as HTMLElement;
      }
      setTooltipState(shouldEnableTooltip(ref));
    }
  }, [tooltipOnlyWithEllipsis]);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 1000);
    }
  }, [copied]);

  const shouldShowTooltip = (tooltipOnlyWithEllipsis ? showTooltip && tooltipText : tooltipText) || copied;

  return (
    <StyledReactCopyToClipboard component="div" data-test={dataTest} {...props}>
      <ActiveDiv hideFocus onClick={onCopyHandler}>
        <Tooltip
          key={copied.toString()}
          title={
            copied
              ? intl.CopyToClipboard.tooltipText.afterCopy
              : (tooltipText ?? intl.CopyToClipboard.tooltipText.beforeCopy)
          }
          placement="top"
          disableHoverListener={!shouldShowTooltip}
        >
          <StyledContent $fullWidth={!!enableEllipsis} onMouseEnter={onMouseEnter}>
            <StyledText $wrapText={!!wrapText} ref={typographyRef as unknown as MutableRefObject<HTMLDivElement>}>
              <span data-test="copy-clipboard-content">{children}</span>
            </StyledText>
            {copied ? (
              <Icon size="small" color="colorSuccess" className="copy-icon" name="check_circle" />
            ) : (
              <Icon size="small" className="copy-icon" name="file_copy" />
            )}
          </StyledContent>
        </Tooltip>
      </ActiveDiv>
    </StyledReactCopyToClipboard>
  );
};

const StyledReactCopyToClipboard = styled(Box)`
  width: calc(100% - ${ICON_SIZE}px);
  > span {
    width: 100%;
  }
`;

export const StyledContent = styled('div')<{ $fullWidth: boolean }>`
  cursor: pointer;
  position: relative;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacingSize.s};
    
  .copy-icon {
    opacity: 0;
    transition: opacity 0.3s ease 0s;
  }
  &:hover {
    .copy-icon {
      opacity: 1;
    }
  }
`;

export const StyledText = styled(Typography)<{ $wrapText: boolean }>`
  vertical-align: middle;
  display: inline-block;
  overflow: hidden;
  white-space: ${({ $wrapText }) => ($wrapText ? 'wrap' : 'nowrap')};
  text-overflow: ellipsis;
  span {
    overflow: hidden;
    white-space: ${({ $wrapText }) => ($wrapText ? 'wrap' : 'nowrap')};
    text-overflow: ellipsis;
  }
  width: inherit;
  &:hover {
    text-decoration: underline ${({ theme }) => theme.tokens.colorPrimary};
  }
`;

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {}
};
